import React, { useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { API_BASE, IS_GUEST } from '../config';
import { getRecaptchaToken } from '../services/recaptcha';

// GuestClaim — the salon signup (Khari, 2026-08-02: "they will need to go
// through auth in the setup — right before purchase in the salon").
//
// The guest arrived free: took Lyra's call, rode with Spor1, answered his
// questions. Those answers live in guestFields. HERE — at the exact point
// money begins — the account happens, as conventional UI, which is where
// design law 1 says conventional UI belongs.
//
// NO NEW BACKEND. This panel orchestrates the endpoints that already exist:
//   1. POST /api/auth/register     (handle_base = their artist name)
//   2. POST /api/auth/verify-email (code from their inbox → the JWT)
//   3. POST /api/avatars           (name + the ride's fields → the shell)
//   4. quest reconciliation        (arrival + ride reported as done — they
//      genuinely did them; M8's provenance seeding marks the fields authored)
// then the token lands in world:authToken and the world reboots as a member
// standing in the salon, where Sassy takes over and charges like she should.

const call = async (path, body, token) => {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', headers, body: JSON.stringify(body || {}) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `${path} ${res.status}`);
  return data;
};

export default function GuestClaim() {
  const interior = useWorldStore((s) => s.interior);
  const guestFields = useWorldStore((s) => s.guestFields);
  const guestProgress = useWorldStore((s) => s.guestProgress);

  const [phase, setPhase] = useState('form');   // form | code | working | done
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [artistName, setArtistName] = useState(guestFields.artist_name || '');
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  // The session from a successful verify, held so a retry resumes AFTER it.
  // Verification is one-shot: once the code is accepted, asking again answers
  // "Email already verified" — so a token dropped on a later failure (an
  // avatar limit, a network blip) used to leave the player permanently stuck
  // between a verified account and a world that still thought they were a guest.
  const [claimToken, setClaimToken] = useState(null);

  if (!IS_GUEST || interior !== 'salon') return null;

  async function register() {
    if (busy) return;
    const name = artistName.trim();
    if (!email.trim() || !password || !name) { setNote('Email, password, and your name — all three.'); return; }
    setBusy(true); setNote('');
    try {
      // The API verifies this whenever its secret key is set — including
      // Khari's local dev API — so the salon signs the same bot check the
      // website's register form does.
      const recaptcha_token = await getRecaptchaToken('register');
      await call('/api/auth/register', {
        email: email.trim(), password, name, handle_base: name, user_type: 'creator', recaptcha_token,
      });
      setPhase('code');
      setNote('');
    } catch (err) {
      setNote(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (busy) return;
    if (!claimToken && !code.trim()) { setNote('The code from your inbox.'); return; }
    setBusy(true); setNote('');
    let stage = 'verify';
    try {
      let token = claimToken;
      if (!token) {
        const v = await call('/api/auth/verify-email', { email: email.trim(), code: code.trim() });
        token = v?.token;
        if (!token) throw new Error('Verified, but no session came back — try signing in on the website.');
        setClaimToken(token);
        // Saved NOW, not after the avatar: the account exists from this moment,
        // and a reload in between must come back signed in, not as a guest who
        // can never verify again. (Phone.jsx also stops ringing once a token is
        // saved, which is what closes the Act 0 replay window.)
        try { localStorage.setItem('world:authToken', token); } catch { /* private mode */ }
      }
      stage = 'avatar';
      setPhase('working');

      // The shell, from the ride's answers — the interview WAS the intake form.
      const av = await call('/api/avatars', {
        name: artistName.trim(),
        bio: guestFields.bio || undefined,
        main_genre: guestFields.main_genre || undefined,
        gender_presentation: guestFields.gender_presentation || undefined,
      }, token);
      const avatarId = av?.avatar?.avatar_id || av?.avatar_id || null;

      // Reconcile the chain with what actually happened: they took the call
      // and they rode. Reported now, as the member they just became, so the
      // salon is their NEXT step — the phone must not re-ring for a call
      // they already answered. Best-effort: a miss self-heals on later syncs.
      if (avatarId) {
        // Only what they actually DID. The panel also shows to a guest who
        // walked into the salon without calling or riding, and marking those
        // beats done would skip Lyra's call and Spor1's ride for good.
        const q = (slug, body) => call(`/api/quests/${slug}`, { avatarId, ...(body || {}) }, token).catch(() => ({}));
        if (guestProgress?.arrivalDone) {
          await q('come_up_arrival/start');
          await q('come_up_arrival/progress', { objectiveType: 'talk_to', targetRef: 'lyra' });
          await q('come_up_arrival/sync');
        }
        if (guestProgress?.rideDone) {
          await q('come_up_the_ride/start');
          await q('come_up_the_ride/progress', { objectiveType: 'goto', targetRef: 'ride_spor1' });
          await q('come_up_the_ride/sync');
        }
        // Wake up AS the artist just made. Without this the socket falls back
        // to "your first avatar with a picture", and a brand-new artist has no
        // picture yet — so the member woke up as nobody in particular.
        try { localStorage.setItem('world:appearAs', String(avatarId)); } catch { /* private mode */ }
      }

      // Become the member: token saved, then the world reboots standing
      // exactly where they are — in the salon, where Sassy waits.
      //
      // clearGuestState happens INSIDE the timeout, immediately before the
      // navigation (Khari's bug, 2026-08-03: clearing it up front wiped
      // arrivalDone while the old page was still alive, and the phone
      // re-synthesized Act 0 and RANG in the 900ms gap — then the reload hit
      // and the whole thing read as a crash). Phone.jsx also refuses to ring
      // once a token is saved, so this window is closed from both sides.
      try { localStorage.setItem('world:authToken', token); } catch { /* private mode */ }
      setPhase('done');
      const u = new URL(window.location.href);
      u.searchParams.set('enter', 'salon');
      setTimeout(() => {
        useWorldStore.getState().clearGuestState();
        window.location.href = u.pathname + u.search + u.hash;
      }, 1400);
    } catch (err) {
      // Back to the step that failed. After a good verify that is the ARTIST
      // step: the button now retries only what is left, never the code.
      setPhase('code');
      if (stage === 'avatar') {
        setNote(/limit/i.test(err.message)
          ? `Your account is made — but it's already at its artist limit (${err.message}). Sign in on the website to manage your artists, or continue as yourself.`
          : `Your account is made, but setting up your artist hit a snag: ${err.message}. Try again — you won't need a new code.`);
      } else {
        setNote(err.message);
      }
    } finally {
      setBusy(false);
    }
  }

  // Signed in with no new artist (e.g. an avatar limit): come in as the member
  // anyway, with whatever artists the account already has.
  function continueAsMember() {
    const u = new URL(window.location.href);
    u.searchParams.set('enter', 'salon');
    useWorldStore.getState().clearGuestState();
    window.location.href = u.pathname + u.search + u.hash;
  }

  return (
    <div style={wrap}>
      <div style={{ font: '800 14px system-ui', letterSpacing: 0.3 }}>💇 Sassy takes clients by name</div>
      <div style={{ font: '600 12px system-ui', opacity: 0.65, lineHeight: 1.5, margin: '6px 0 12px' }}>
        {guestProgress?.rideDone
          ? 'Spor1 vouched for you. Make it official and the chair is yours — the treatment is paid here, so the account happens here.'
          : 'The chair is for clients. Make it official — takes a minute.'}
      </div>

      {phase === 'form' && (
        <>
          <input style={input} placeholder="Your artist name" value={artistName} onChange={(e) => setArtistName(e.target.value)} />
          <input style={input} placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input style={input} placeholder="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button style={{ ...cta, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={register}>
            {busy ? 'One second…' : 'Make it official'}
          </button>
        </>
      )}

      {phase === 'code' && !claimToken && (
        <>
          <div style={{ font: '600 12px system-ui', opacity: 0.75, marginBottom: 8 }}>
            Code's in your inbox — {email.trim()}.
          </div>
          <input style={input} placeholder="4-digit code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} />
          <button style={{ ...cta, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={verify}>
            {busy ? 'Checking…' : "That's me"}
          </button>
        </>
      )}

      {phase === 'code' && claimToken && (
        <>
          <button style={{ ...cta, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={verify}>
            {busy ? 'Trying again…' : 'Set up my artist'}
          </button>
          <button onClick={continueAsMember} style={{
            width: '100%', marginTop: 8, padding: '9px 0', borderRadius: 999, cursor: 'pointer',
            border: '1px solid rgba(240,166,192,0.45)', background: 'none', color: '#ffe9c4', font: '700 12.5px system-ui',
          }}>Continue as me</button>
        </>
      )}

      {phase === 'working' && <div style={{ font: '600 12.5px system-ui', opacity: 0.75 }}>Setting you up…</div>}
      {/* The reload is the DESIGN (the world reboots as the member — the
          token is read at boot), so the copy has to claim it: an unannounced
          hard reload reads as a crash (Khari, 2026-08-03: "the app quit"). */}
      {phase === 'done' && (
        <div style={{ font: '700 13px system-ui', color: '#7dff9d' }}>
          You're in. The city's restarting as you — one second…
        </div>
      )}

      {note && <div style={{ font: '600 11.5px system-ui', color: '#ffd27d', marginTop: 8, lineHeight: 1.4 }}>{note}</div>}
    </div>
  );
}

const wrap = {
  position: 'fixed', left: '50%', transform: 'translateX(-50%)',
  bottom: 'max(18px, env(safe-area-inset-bottom))', zIndex: 72,
  width: 'min(340px, calc(100vw - 32px))', padding: '14px 16px',
  background: 'rgba(14,9,6,0.95)', border: '1px solid rgba(240,166,192,0.55)', borderRadius: 16,
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  color: '#ffe9c4', fontFamily: 'system-ui, sans-serif', pointerEvents: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
};
const input = {
  display: 'block', width: '100%', boxSizing: 'border-box', marginBottom: 8,
  padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(240,166,192,0.35)',
  background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 14, fontFamily: 'inherit', outline: 'none',
};
const cta = {
  width: '100%', padding: '11px 0', borderRadius: 999, border: 'none', cursor: 'pointer',
  font: '800 13.5px system-ui', background: 'linear-gradient(180deg,#f0a6c0,#ff5e78)', color: '#1a0f00',
};
